import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import moment from "moment-timezone";

interface ScheduledCallbackState {
  timetableMapping: ScheduledCallback.TimetableMapping,
  unsavedChanges: boolean,
  editable: boolean,
  timetableList: HOOT.TimetableLabel[],
  mondayOptions: string[],
  tuesdayOptions: string[],
  wednesdayOptions: string[],
  thursdayOptions: string[],
  fridayOptions: string[],
  saturdayOptions: string[],
  sundayOptions: string[]
}

const initialState: ScheduledCallbackState = {
  timetableMapping: {
    timetableId: -1,
    timetableName: "Default",
    days: [
      {id: "Monday", name: "Monday", hours: [], buckets: []},
      {id: "Tuesday", name: "Tuesday", hours: [], buckets: []},
      {id: "Wednesday", name: "Wednesday", hours: [], buckets: []},
      {id: "Thursday", name: "Thursday", hours: [], buckets: []},
      {id: "Friday", name: "Friday", hours: [], buckets: []},
      {id: "Saturday", name: "Saturday", hours: [], buckets: []},
      {id: "Sunday", name: "Sunday", hours: [], buckets: []},
    ],
    skills: [],
    exceptions: []
  },
  unsavedChanges: false,
  editable: false,
  timetableList: [],
  mondayOptions: [],
  tuesdayOptions: [],
  wednesdayOptions: [],
  thursdayOptions: [],
  fridayOptions: [],
  saturdayOptions: [],
  sundayOptions: []
}

const ScheduledCallbackSlice = createSlice({
  name: "scheduledCallback",
  initialState,
  reducers: {
    setTimetableMapping: (state: ScheduledCallbackState, action: PayloadAction<ScheduledCallback.TimetableMapping>) => {
      state.timetableMapping = action.payload;
      return state;
    },
    clearOptions: (state: ScheduledCallbackState, action: PayloadAction) => {
      state.mondayOptions = [];
      state.tuesdayOptions = [];
      state.wednesdayOptions = [];
      state.thursdayOptions = [];
      state.fridayOptions = [];
      state.saturdayOptions = [];
      state.sundayOptions = [];
      return state;
    },
    setMondayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.mondayOptions = action.payload;
      return state;
    },
    setTuesdayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.tuesdayOptions = action.payload;
      return state;
    },
    setWednesdayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.wednesdayOptions = action.payload;
      return state;
    },
    setThursdayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.thursdayOptions = action.payload;
      return state;
    },
    setFridayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.fridayOptions = action.payload;
      return state;
    },
    setSaturdayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.saturdayOptions = action.payload;
      return state;
    },
    setSundayOptions: (state: ScheduledCallbackState, action: PayloadAction<string[]>) => {
      state.sundayOptions = action.payload;
      return state;
    },
    setTimetableMappingToDefault: (state: ScheduledCallbackState) => {
      state.timetableMapping = initialState.timetableMapping;
      state.editable = initialState.editable;
      state.unsavedChanges = initialState.unsavedChanges;
      return state;
    },
    setTimetableList: (state : ScheduledCallbackState, action : PayloadAction<HOOT.TimetableLabel[]>) => {
      state.timetableList = action.payload;
      return state;
    },
    setUnsavedChanges: (state : ScheduledCallbackState, action : PayloadAction<boolean>) => {
      state.unsavedChanges = action.payload;
      return state;
    },
    setEditable: (state : ScheduledCallbackState, action : PayloadAction<boolean>) => {
      state.editable = action.payload;
      return state;
    },
    addBucket: (state : ScheduledCallbackState, action: PayloadAction<{index: number, start: string, end: string}>) => {
      state.timetableMapping.days[action.payload.index].buckets.push({id: "0", startTime: action.payload.start, endTime: action.payload.end, capacity: 0});
      return state;
    },
    addExceptionBucket: (state: ScheduledCallbackState, action: PayloadAction<{id: string, start: string, end: string}>) => {
      state.timetableMapping.exceptions.find((exception : ScheduledCallback.Exception) => exception.id === action.payload.id)?.buckets.push({id: "0", startTime: action.payload.start, endTime: action.payload.end, capacity: 0});
      return state;
    },
    removeBucket: (state: ScheduledCallbackState, action: PayloadAction<{dayIndex : number, bucketIndex : number}>) => {
      state.timetableMapping.days[action.payload.dayIndex].buckets.splice(action.payload.bucketIndex, 1);
      return state;
    },
    changeStartTime: (state: ScheduledCallbackState, action: PayloadAction<{dayIndex: number, bucketIndex: number, newTime: String}>) => {
      state.timetableMapping.days[action.payload.dayIndex].buckets[action.payload.bucketIndex].startTime = action.payload.newTime;
      return state;
    },
    changeEndTime: (state: ScheduledCallbackState, action: PayloadAction<{dayIndex: number, bucketIndex: number, newTime: String}>) => {
      state.timetableMapping.days[action.payload.dayIndex].buckets[action.payload.bucketIndex].endTime = action.payload.newTime;
      return state;
    },
    changeCapacity: (state: ScheduledCallbackState, action: PayloadAction<{dayIndex: number, bucketIndex: number, newCapacity: number}>) => {
      state.timetableMapping.days[action.payload.dayIndex].buckets[action.payload.bucketIndex].capacity = action.payload.newCapacity;
      return state;
    },
    changeExceptionStartTime: (state: ScheduledCallbackState, action: PayloadAction<{exceptionId: string, bucketIndex: number, newTime: String}>) => {
      let exception : ScheduledCallback.Exception | undefined = state.timetableMapping.exceptions.find((exception: ScheduledCallback.Exception) => exception.id === action.payload.exceptionId);
      if(exception !== undefined){
        exception.buckets[action.payload.bucketIndex].startTime = action.payload.newTime;
      }
      return state;
    },
    changeExceptionEndTime: (state: ScheduledCallbackState, action: PayloadAction<{exceptionId: string, bucketIndex: number, newTime: String}>) => {
      let exception : ScheduledCallback.Exception | undefined = state.timetableMapping.exceptions.find((exception: ScheduledCallback.Exception) => exception.id === action.payload.exceptionId);
      if(exception !== undefined){
        exception.buckets[action.payload.bucketIndex].endTime = action.payload.newTime;
      }
      return state;
    },
    changeExceptionCapacity: (state: ScheduledCallbackState, action: PayloadAction<{exceptionId: string, bucketIndex: number, newCapacity: number}>) => {
      let exception : ScheduledCallback.Exception | undefined = state.timetableMapping.exceptions.find((exception: ScheduledCallback.Exception) => exception.id === action.payload.exceptionId);
      if(exception !== undefined){
        exception.buckets[action.payload.bucketIndex].capacity = action.payload.newCapacity;
      }
      return state;
    },
    removeExceptionBucket: (state: ScheduledCallbackState, action: PayloadAction<{exceptionId: string, bucketIndex: number}>) => {
      let exception : ScheduledCallback.Exception | undefined = state.timetableMapping.exceptions.find((exception: ScheduledCallback.Exception) => exception.id === action.payload.exceptionId);
      if(exception !== undefined){
        exception.buckets.splice(action.payload.bucketIndex, 1);
      }
      return state;
    },
    addSkill: (state: ScheduledCallbackState, action: PayloadAction<ScheduledCallback.Skill>) => {
      state.timetableMapping.skills.push({alias: action.payload.alias, skillNumber: action.payload.skillNumber});
      return state;
    },
    sortBuckets: (state: ScheduledCallbackState) => {
      state.timetableMapping.days.forEach((day) => { day.buckets = day.buckets.sort((a, b) => moment(a.startTime.toString(), "hh:mm A").valueOf() - moment(b.startTime.toString(), "hh:mm A").valueOf()) })
      state.timetableMapping.exceptions.forEach((day) => {day.buckets = day.buckets.sort((a, b) => moment(a.startTime.toString(), "hh:mm A").valueOf() - moment(b.startTime.toString(), "hh:mm A").valueOf()) })
      return state;
    },
    removeSkill: (state: ScheduledCallbackState, action: PayloadAction<number>) => {
      let deleteIndex = state.timetableMapping.skills.findIndex((skill) => skill.skillNumber === action.payload);
      if(deleteIndex > -1) {
        state.timetableMapping.skills.splice(deleteIndex, 1);
      }
      return state;
    }
  }
});

export default ScheduledCallbackSlice;
